import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type {
  BlockerView,
  ScheduleChangeReason,
  WorkItemDetail,
} from "../../shared/types/api";
import {
  REASON_GROUPS,
  REASON_LABELS,
  formatDays,
  isExternal,
} from "./changeVocabulary";

/**
 * Sets the dates, or moves them — and knows which of the two is happening (§56, §57, §58, §59).
 *
 * <p>An item with no dates has nothing to move. Asking for a reason there meant somebody writing
 * down the first plan had to pick a cause for a delay that had not happened, and the schedule
 * report filled with slips from nothing to a date. So when the item has no committed dates this
 * panel is a planning form: dates, estimate, no reason. Once they exist it becomes the change
 * record it always was.
 *
 * <p>The reason is a code from §59's list rather than free text, because §60 counts them. Until
 * this phase the panel asked for prose and threw it away — which is the worst of both, since the
 * form looked answered and the report had nothing behind it.
 *
 * <p>Committed and forecast are asked for separately because they answer different questions:
 * committed is what has been promised, forecast is what is actually expected. Teams often know
 * the second before they are willing to change the first, and one date field forces them to
 * choose which truth to record.
 */
export function RescheduleDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [committedStart, setCommittedStart] = useState("");
  const [committedDue, setCommittedDue] = useState("");
  const [forecastDue, setForecastDue] = useState("");
  const [estimate, setEstimate] = useState("");
  const [reasonCode, setReasonCode] = useState<ScheduleChangeReason | "">("");
  const [comment, setComment] = useState("");
  const [impact, setImpact] = useState("");
  const [impactHours, setImpactHours] = useState("");
  const [blockerId, setBlockerId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const blockers = useQuery({
    queryKey: ["work-item-blockers", item?.id],
    queryFn: () =>
      api.get<BlockerView[]>(`/api/v1/work-items/${item?.id}/blockers`),
    enabled: Boolean(item),
  });

  useEffect(() => {
    if (item) {
      setCommittedStart(item.committedStartDate ?? "");
      setCommittedDue(item.committedDueDate ?? "");
      setForecastDue(item.forecastDueDate ?? "");
      setEstimate("");
      setReasonCode("");
      setComment("");
      setImpact("");
      setImpactHours("");
      setBlockerId("");
      setError(null);
    }
  }, [item]);

  /*
   * Nothing has been promised yet, so nothing can have slipped. The panel asks for a plan rather
   * than for a reason, and the estimate is asked for alongside because the same two moments need
   * both: an item cannot be taken on or started without them.
   */
  const planning =
    item != null &&
    item.committedStartDate == null &&
    item.committedDueDate == null;
  const needsEstimate = planning && item?.originalEstimateHours == null;

  const changedFields = item
    ? [
        committedStart !== (item.committedStartDate ?? ""),
        committedDue !== (item.committedDueDate ?? ""),
        forecastDue !== (item.forecastDueDate ?? ""),
      ].filter(Boolean).length
    : 0;

  const commentRequired = reasonCode === "OTHER";

  const submit = async () => {
    if (!item) {
      return;
    }
    if (planning) {
      await savePlan(item.id);
      return;
    }
    if (changedFields === 0) {
      setError("None of those dates are changing yet.");
      return;
    }
    if (reasonCode === "") {
      setError("Pick a reason — it is what the schedule report is built from.");
      return;
    }
    if (commentRequired && comment.trim() === "") {
      setError('"Other" needs a comment saying what the reason actually was.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item.id}/reschedule`, {
        committedStartDate: committedStart || null,
        committedDueDate: committedDue || null,
        forecastDueDate: forecastDue || null,
        reasonCode,
        reasonComment: comment || null,
        impactDescription: impact || null,
        impactHours: impactHours === "" ? null : Number(impactHours),
        linkedBlockerId: blockerId || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not move those dates.",
      );
    } finally {
      setSaving(false);
    }
  };

  /** The first plan, which goes to its own endpoint precisely because it is not a change. */
  const savePlan = async (id: string) => {
    if (committedStart === "" || committedDue === "") {
      setError(
        "Give both dates. Work that has one end open cannot be late, or on time.",
      );
      return;
    }
    if (needsEstimate && estimate.trim() === "") {
      setError(
        "Give an estimate as well — it is what capacity and the burndown are built from.",
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${id}/plan`, {
        originalEstimateHours: needsEstimate ? Number(estimate) : null,
        startDate: committedStart,
        dueDate: committedDue,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not set those dates.",
      );
    } finally {
      setSaving(false);
    }
  };

  const original = item?.originalDueDate ?? null;
  const drift =
    original && committedDue ? daysBetween(original, committedDue) : null;
  const movingCommitmentOut =
    item?.committedDueDate != null &&
    committedDue !== "" &&
    daysBetween(item.committedDueDate, committedDue) > 0;

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title={planning ? "Set the dates" : "Move the dates"}
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving
              ? "Saving…"
              : planning
                ? "Set the plan"
                : "Record the change"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {planning ? (
          <InlineAlert tone="info">
            This work has no dates yet, so nothing here is a change and no
            reason is asked for. What you set now becomes the original plan —
            everything afterwards is measured against it, and moving it later
            carries a reason.
          </InlineAlert>
        ) : (
          <div className="overflow-hidden rounded-lg border border-ink-200">
            <ReadOnlyRow
              label="Originally planned to start"
              value={item?.originalStartDate}
            />
            <ReadOnlyRow label="Originally due" value={original} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field
            label={planning ? "Start" : "Committed start"}
            required={planning}
          >
            <Input
              type="date"
              value={committedStart}
              onChange={(event) => setCommittedStart(event.target.value)}
            />
          </Field>
          <Field label={planning ? "Due" : "Committed due"} required={planning}>
            <Input
              type="date"
              value={committedDue}
              onChange={(event) => setCommittedDue(event.target.value)}
            />
          </Field>
        </div>

        {needsEstimate && (
          <Field
            label="Estimate (hours)"
            required
            hint="Recorded once and then fixed. Capacity, the burndown and every variance figure are built from it; re-estimating later keeps this figure beside the new one."
          >
            <Input
              type="number"
              min="0"
              step="0.5"
              value={estimate}
              onChange={(event) => setEstimate(event.target.value)}
            />
          </Field>
        )}

        {drift != null && drift !== 0 && (
          <InlineAlert tone={drift > 0 ? "warning" : "success"}>
            That is {formatDays(drift)} than first planned. The original stays
            on record, so the movement is reportable rather than invisible.
          </InlineAlert>
        )}

        {/*
         * Both absent while planning. A forecast says "this will not land when promised", which
         * is not something anybody can mean about a date they are agreeing in the same breath —
         * and a reason for a delay that has not happened is the thing this panel got wrong.
         */}
        {!planning && (
          <>
            <Field
              label="Forecast due"
              hint="Where it is actually expected to land, if that differs from what is promised. Moving this needs nobody's sign-off."
            >
              <Input
                type="date"
                value={forecastDue}
                onChange={(event) => setForecastDue(event.target.value)}
              />
            </Field>

            <Field
              label="Why"
              required
              hint="A code rather than a sentence, because the schedule report counts them."
            >
              <Select
                value={reasonCode}
                onChange={(event) =>
                  setReasonCode(event.target.value as ScheduleChangeReason)
                }
              >
                <option value="">Pick a reason…</option>
                {REASON_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.codes.map((code) => (
                      <option key={code} value={code}>
                        {REASON_LABELS[code]}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </Field>

            <Field
              label="What happened"
              required={commentRequired}
              hint={
                commentRequired
                  ? '"Other" counts towards nothing on its own, so these words are the whole record.'
                  : "Optional. The code says which kind of delay; this says which one."
              }
            >
              <Textarea
                rows={2}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="External API access was delivered late."
              />
            </Field>

            {reasonCode !== "" && isExternal(reasonCode) && (
              <InlineAlert tone="info">
                This counts as delay from outside the team, and is reported
                separately from our own.
              </InlineAlert>
            )}

            {blockers.data && blockers.data.length > 0 && (
              <Field
                label="Caused by a blocker"
                hint="Links the slip to the thing that caused it, so the blocker's cost is visible."
              >
                <Select
                  value={blockerId}
                  onChange={(event) => setBlockerId(event.target.value)}
                >
                  <option value="">Not linked to a blocker</option>
                  {blockers.data.map((blocker) => (
                    <option key={blocker.id} value={blocker.id}>
                      {blocker.title}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
              <Field
                label="Impact"
                hint="What this costs beyond the date itself."
              >
                <Input
                  value={impact}
                  onChange={(event) => setImpact(event.target.value)}
                  placeholder="QA window compressed to two days"
                />
              </Field>
              <Field label="Extra hours">
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={impactHours}
                  onChange={(event) => setImpactHours(event.target.value)}
                />
              </Field>
            </div>

            {movingCommitmentOut && (
              <InlineAlert tone="warning">
                Moving a commitment outwards may need sign-off from somebody
                else before it is settled — the record is kept either way, and
                shows as waiting until it is decided.
              </InlineAlert>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

function ReadOnlyRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0">
      <span className="text-ink-500">{label}</span>
      <span className={value ? "tnum text-ink-900" : "text-ink-400"}>
        {value ?? "not set"}
      </span>
    </div>
  );
}

/**
 * Whole days from one ISO date to another.
 *
 * <p>Parsed as UTC midnight so a daylight-saving change between the two cannot land the answer a
 * day out.
 */
function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return 0;
  }
  return Math.round((end - start) / 86_400_000);
}
